import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const NavLink = ({ to, children }) => (
  <Link to={to} className="text-sm font-medium text-charcoal/80 hover:text-rose-500 transition-colors" data-testid={`nav-${to.replace(/\//g, "")}`}>
    {children}
  </Link>
);

export default function Navbar() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const links = [
    { to: "/armocromia", label: t("nav.armocromia") },
    { to: "/catalog/makeup", label: t("nav.makeup") },
    { to: "/catalog/skincare", label: t("nav.skincare") },
    { to: "/catalog/fashion", label: t("nav.fashion") },
    { to: "/outfits", label: t("nav.outfits") },
    { to: "/hair", label: t("nav.hair") },
    { to: "/guide", label: lang === "it" ? "Guide" : "Guides" },
  ];

  return (
    <header className="sticky top-0 z-50 glass border-b border-rose-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[64px] md:h-[88px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 md:gap-3" data-testid="logo-home-link">
          <img src="/velyrea-logo.png" alt="Velyrea" className="h-11 w-11 md:h-[68px] md:w-[68px] object-contain drop-shadow-sm" />
          <span className="font-serif text-2xl md:text-4xl font-bold tracking-tight text-charcoal">Velyrea</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => <NavLink key={l.to} to={l.to}>{l.label}</NavLink>)}
        </nav>

        <div className="flex items-center gap-3">
          <button onClick={toggle} data-testid="lang-toggle" className="text-xs font-semibold uppercase tracking-widest text-charcoal/70 hover:text-rose-500 border border-rose-200 rounded-full px-3 py-1.5 transition-colors">
            {lang === "it" ? "EN" : "IT"}
          </button>
          <Link to="/quiz" className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-rose-400 hover:bg-rose-500 text-white text-sm font-semibold px-5 py-2.5 transition-all hover:-translate-y-0.5 shadow-sm" data-testid="nav-quiz-btn">
            <Sparkles size={15} /> {t("nav.quiz")}
          </Link>
          {user ? (
            <div className="hidden lg:flex items-center gap-2">
              {user.role === "admin" && <Link to="/admin" className="text-xs font-semibold text-gold-dark" data-testid="nav-admin">{t("nav.admin")}</Link>}
              <Link to="/dashboard" className="p-2 rounded-full hover:bg-rose-50" data-testid="nav-dashboard"><User size={19} /></Link>
              <button onClick={() => { logout(); nav("/"); }} className="p-2 rounded-full hover:bg-rose-50" data-testid="nav-logout"><LogOut size={18} /></button>
            </div>
          ) : (
            <Link to="/login" className="hidden lg:inline text-sm font-medium hover:text-rose-500" data-testid="nav-login">{t("nav.login")}</Link>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-rose-100 px-5 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-charcoal/80 font-medium">{l.label}</Link>
          ))}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="font-medium">{t("nav.dashboard")}</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="font-medium text-gold-dark">{t("nav.admin")}</Link>}
              <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-left font-medium">{t("nav.logout")}</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="font-medium">{t("nav.login")}</Link>
          )}
        </div>
      )}
    </header>
  );
}
